/**
 * Product Details Variant Handler
 *
 * Handles showing/hiding variant-specific content in product details:
 * - Product descriptions
 * - Specification tables
 * - Manual book links
 * - Highlighted content
 *
 * Listens for 'on:variant:change' events from variant-picker component
 */

(function () {
  "use strict";

  class ProductDetailsVariantHandler {
    constructor() {
      this.currentVariantId = null;
      this.init();
    }

    /**
     * Initialize the handler
     */
    init() {
      // Wait for DOM to be ready
      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => this.setupListeners());
      } else {
        this.setupListeners();
      }
    }

    /**
     * Setup event listeners for variant changes
     */
    setupListeners() {
      // Listen for variant change events on the document
      document.addEventListener("on:variant:change", (event) => {
        this.handleVariantChange(event);
      });

      // Set initial state based on URL parameter or selected variant
      this.setInitialState();
    }

    /**
     * Handle variant change event
     * @param {CustomEvent} event - The variant change event
     */
    handleVariantChange(event) {
      const variant = event.detail?.variant;

      if (!variant || !variant.id) {
        // If no variant selected, hide all variant-specific content
        this.updateVariantElements(null);
        return;
      }

      this.currentVariantId = variant.id.toString();
      this.updateVariantElements(this.currentVariantId);
    }

    /**
     * Update all elements with data-variant-id attributes
     * @param {string|null} variantId - The variant ID to show, or null to hide all
     */
    updateVariantElements(variantId) {
      // Find all elements with data-variant-id attribute
      const variantElements = document.querySelectorAll(".product-details [data-variant-id]");

      variantElements.forEach((element) => {
        const elementVariantId = element.getAttribute("data-variant-id");
        const isMatch = variantId && elementVariantId === variantId;

        // Update visibility classes
        if (isMatch) {
          element.classList.remove("hidden");
          element.classList.add("active");
          element.closest(".product-description").classList.add("has-active-variant");
        } else {
          element.classList.add("hidden");
          element.classList.remove("active");
          element.closest(".product-description").classList.remove("has-active-variant");
        }
      });

      // Handle spec-table rows specifically (add highlight class)
      this.updateSpecTableRows(variantId);
    }

    /**
     * Update spec-table rows with highlight class
     * @param {string|null} variantId - The variant ID to highlight, or null to remove all highlights
     */
    updateSpecTableRows(variantId) {
      const specTable = document.querySelector(".spec-table");
      if (!specTable) return;

      const rows = specTable.querySelectorAll("tbody tr[data-table-variant-id]");

      rows.forEach((row) => {
        const rowVariantId = row.getAttribute("data-table-variant-id");
        const isMatch = variantId && rowVariantId === variantId;

        // Add/remove highlight class for visual emphasis
        // Spec table rows remain visible - we only highlight the active one
        if (isMatch) {
          row.classList.add("spec-table-row--active");
          row.classList.remove("spec-table-row--inactive");
        } else {
          row.classList.remove("spec-table-row--active");
          // Don't hide rows - they should all remain visible for comparison
          row.classList.add("spec-table-row--inactive");
        }
      });
    }

    /**
     * Set initial state based on URL parameter or selected variant
     */
    setInitialState() {
      // Wait a bit for variant-picker to initialize
      const urlParams = new URLSearchParams(window.location.search);
      const urlVariantId = urlParams.get("variant");

      if (urlVariantId) {
        this.currentVariantId = urlVariantId;
        this.updateVariantElements(this.currentVariantId);
        return;
      }
    }
  }

  // Initialize the handler
  new ProductDetailsVariantHandler();
})();
